/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NextDueTypeProps {
  id: string;
  name: string;
  dataType: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class NextDueTypeRecord extends Record<NextDueTypeProps>({
  id: null as any,
  name: null as any,
  dataType: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    dataType: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): NextDueTypeRecord {
    if (json == null) {
      throw new Error('NextDueTypeRecord.fromJS: parameter cannot be null');
    }
    return new NextDueTypeRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NextDueTypeJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'NextDueTypeJson.name'),
      dataType: getFromOrThrow(json, (j) => j.dataType, 'NextDueTypeJson.dataType'),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    dataType: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [NextDueTypeRecord, Error[]] {
    if (json == null) {
      throw new Error('NextDueTypeRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NextDueTypeRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NextDueTypeJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'NextDueTypeJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dataType: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.dataType, 'NextDueTypeJson.dataType'),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: NextDueTypeProps) {
    super(props);
    if (props == null) {
      throw new Error('NextDueTypeRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NextDueTypeViewModelBase implements NextDueTypeProps {
  protected readonly _props: NextDueTypeProps;
  private _json?: NextDueTypeProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get dataType(): string {
    return this._props.dataType;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: NextDueTypeProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        dataType: this._props.dataType,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllNextDueTypeEntities = (state: AppState) =>
  state.entityStores.nextDueType.entities;

export const selectNextDueTypeById = (id: string) => (state: AppState) =>
  state.entityStores.nextDueType.entities.get(id);

export const selectNextDueTypeMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.nextDueType.meta.get(id);

export const selectNextDueTypeAndMetadataById = (id: string) =>
  createSelector(
    selectNextDueTypeById(id),
    selectNextDueTypeMetadataById(id),
    (nextDueType, meta) => ({ nextDueType, meta }),
  );

export function selectNextDueTypeEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllNextDueTypeEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, NextDueTypeRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectNextDueTypeEntitiesByName(name: string) {
  return createSelector(selectAllNextDueTypeEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectNextDueTypeEntitiesByDataType(dataType: string) {
  return createSelector(selectAllNextDueTypeEntities, (entities) =>
    entities.filter((e) => e.dataType === dataType).valueSeq(),
  );
}
//#endregion Selectors
